import { SimpleGrid, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { EmptyState } from '@feathr/components';

import * as styles from './CreativesGrid.css';

export interface ICreativesGridProps {
  creatives: DisplayCreative[];
  renderItem: (creative: DisplayCreative) => ReactNode;
  emptyStateMessage?: string;
  emptyStateDescription?: string;
  emptyStateAction?: ReactNode;
}

function CreativesGrid({
  creatives,
  renderItem,
  emptyStateMessage,
  emptyStateDescription,
  emptyStateAction,
}: Readonly<ICreativesGridProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  if (creatives.length === 0) {
    return (
      <EmptyState
        description={
          emptyStateDescription ??
          t(
            'Get started by uploading your first creative. Supported formats include JPG, PNG, and GIF.',
          )
        }
        label={emptyStateMessage ?? t('No creatives yet')}
      >
        {emptyStateAction}
      </EmptyState>
    );
  }

  return (
    <SimpleGrid
      className={styles.root}
      cols={{ base: 1, sm: 2, md: 3 }}
      data-name={'creatives-grid'}
      spacing={theme.spacing['4']}
    >
      {creatives.map(renderItem)}
    </SimpleGrid>
  );
}

export default observer(CreativesGrid);
